import {
  Box,
  Checkbox,
  Chip,
  DialogActions,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  getClientInfoForPublication,
  PublicationDocumentInfo,
  publishClientDocument,
} from 'lib/api/documentPublication';
import CustomButton from 'lib/designSystem/Button';
import Disclaimer from 'lib/designSystem/Disclaimer';
import { applyDefaults, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { FormProps } from '../..';

type PublicationFormInfo = {
  id: string;
  businessIdentifier: string;
  businessName: string;
  folio: string;
  amount: number;
  type: string;
  issuedDate: string;
  paymentDate: string;
};

const emptyDocument = {
  id: '',
  businessIdentifier: '',
  businessName: '',
  folio: '',
  amount: 0,
  type: '',
  issuedDate: '',
  paymentDate: '',
};

const emptyContactInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

const ClientPublicationForm: React.FC<FormProps> = ({
  documentId,
  setLoading,
  setOpen,
  handleSuccessClose,
  setIdentifier,
}: FormProps) => {
  const dispatch = useDispatch();
  const [publicationDocumentInfo, setPublicationDocumentInfo] =
    useState<PublicationFormInfo>(emptyDocument);
  const [contactForm, setContactForm] = useState(emptyContactInfo);
  const [cosigners, setCosigners] = useState<string[]>([]);
  const [currentCosigner, setCurrentCosigner] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<Record<string, boolean>>({
    Contrato: true,
    'Pagare Adeudado': false,
  });

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPublicationDocumentInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddCosigner = () => {
    if (currentCosigner.trim() && !cosigners.includes(currentCosigner.trim())) {
      setCosigners([...cosigners, currentCosigner.trim()]);
      setCurrentCosigner('');
    }
  };

  const handleRemoveName = (cosignerToRemove: string) => {
    setCosigners(
      cosigners.filter((cosigners) => cosigners !== cosignerToRemove),
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCosigner();
    }
  };

  const generateForm = (
    document: PublicationDocumentInfo & {
      businessName: string;
      paymentDate: string | null;
    },
  ) => {
    document.folio = document.folio.split('-')[0];
    document.issuedDate = document.issuedDate.substring(0, 10);
    document.paymentDate = document.paymentDate?.substring(0, 10) ?? '';
    return applyDefaults<PublicationFormInfo>(
      { ...document, paymentDate: document.paymentDate },
      emptyDocument,
    );
  };

  const getDocumentInfo = async () => {
    if (!documentId) return;
    const response = await getClientInfoForPublication(dispatch, documentId);
    if (!response) return;
    const { document, contactInfo } = response;
    setPublicationDocumentInfo(generateForm(document));
    setContactForm({ ...emptyContactInfo, ...contactInfo });
  };

  const handlePublish = async () => {
    try {
      setLoading(true);
      const payload = {
        document: publicationDocumentInfo,
        contact: contactForm,
        cosigners,
        publicationTypes: Object.entries(selectedTypes).map(([key, value]) => {
          if (value) return key;
        }),
      };
      await publishClientDocument(dispatch, payload);
      setLoading(false);
      setIdentifier(publicationDocumentInfo.businessIdentifier);
      handleSuccessClose();
    } catch (error) {
      setLoading(false);
      showAlert(
        dispatch,
        'error',
        'Hubo un problema al publicar el documento, revisa que los campos sean correctos',
      );
    }
  };

  useEffect(() => {
    if (!documentId) return;
    try {
      setLoading(true);
      void getDocumentInfo();
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  }, [documentId]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Folio"
            variant="outlined"
            required
            name="folio"
            value={publicationDocumentInfo.folio}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Monto"
            variant="outlined"
            required
            name="amount"
            value={publicationDocumentInfo.amount}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="RUT emisor"
            variant="outlined"
            required
            name="businessIdentifier"
            value={publicationDocumentInfo.businessIdentifier}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Razón social emisor"
            variant="outlined"
            required
            name="name"
            value={publicationDocumentInfo.businessName}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Fecha emisión documento"
            type="date"
            name="issuedDate"
            value={publicationDocumentInfo.issuedDate}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Fecha vencimiento documento"
            type="date"
            name="paymentDate"
            value={publicationDocumentInfo.paymentDate}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Nombre contacto emisor"
            variant="outlined"
            required
            name="firstName"
            value={contactForm.firstName}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Apellido contacto emisor"
            variant="outlined"
            required
            name="lastName"
            value={contactForm.lastName}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Teléfono contacto emisor"
            variant="outlined"
            required
            name="phone"
            value={contactForm.phone}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Correo contacto emisor"
            variant="outlined"
            required
            name="email"
            value={contactForm.email}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Ingresar avales (opcional)"
            variant="outlined"
            placeholder="Escribe el RUT del aval y presiona Enter"
            value={currentCosigner}
            onChange={(e) => setCurrentCosigner(e.target.value)}
            onKeyPress={handleKeyPress}
            helperText="Agrega los RUT de los avales que necesites incluir en la publicación."
          />
          {cosigners.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {cosigners.map((cosigner, index) => (
                <Chip
                  key={index}
                  label={cosigner}
                  onDelete={() => handleRemoveName(cosigner)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Grid>
        <Grid item xs={6}>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <Checkbox
              checked={selectedTypes['Contrato']}
              onChange={(e) =>
                setSelectedTypes({
                  ...selectedTypes,
                  Contrato: e.target.checked,
                })
              }
              color="primary"
            />
            <Typography>Publicar por contrato impago</Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <Checkbox
              checked={selectedTypes['Pagare Adeudado']}
              onChange={(e) =>
                setSelectedTypes({
                  ...selectedTypes,
                  'Pagare Adeudado': e.target.checked,
                })
              }
              color="primary"
            />
            <Typography>Publicar por pagaré</Typography>
          </Box>
        </Grid>
      </Grid>
      <Disclaimer text="Recuerda que para habilitar la publicación de un cliente debes enviar un contrato marco (o un ejemplo)." />
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton color="primary" onClick={handlePublish}>
          Publicar
        </CustomButton>
      </DialogActions>
    </div>
  );
};

export default ClientPublicationForm;
